import React from 'react'
import countriesLib from 'i18n-iso-countries'
import nationalitiesLib from 'i18n-nationality'
import { useEffect, useState, useContext } from 'react'
import { toast } from 'react-toastify'
import { addType, getUniqueTypes } from '../services/Node/index'
import Loader from './Loader'
import Types from './Types'
import { Button } from 'react-bootstrap'
import DataContext from '../store/data-context'
import AddTypeModal from './AddTypeModal'

function TableHeader() {
    const { type, setType, tableData, setTableData, locales } = useContext(DataContext)
    const [uniqueTypes, setUniqueTypes] = useState([])
    const [countries, setCountries] = useState([])
    const [nationalities, setNationalities] = useState([])
    const [btnClicked, setBtnClicked] = useState(false)
    const [rows, setRows] = useState([{ value: '', locale: locales.filter(item => item.isDefault)[0], id: new Date().toISOString() }])
    const [isLoaded, setIsLoaded] = useState(false)
    const [showModal, setShowModal] = useState(false)
    const isMultiSelect = (type.value === 'nationality' || type.value === 'location')

    useEffect(() => {
        setIsLoaded(false)
        const fetchUniqueTypes = async () => {
            const resp = await getUniqueTypes()
            if (resp.success)
                setUniqueTypes(resp.data)
            else
                throw new Error(resp.error)
        }

        const setUpCountries = () => {
            for (const { value } of locales) {
                try {
                    const lang = require(`i18n-iso-countries/langs/${value}.json`)
                    countriesLib.registerLocale(lang)
                } catch (error) {
                    // Language not supported by the library
                }
            }
            const allCountries = countriesLib.getNames('en', { select: 'official' })
            setCountries(Object.keys(allCountries).map(key => ({ value: key, label: allCountries[key] })))
        }

        const setUpNationalities = () => {
            for (const { value } of locales) {
                try {
                    const lang = require(`i18n-nationality/langs/${value}.json`)
                    nationalitiesLib.registerLocale(lang);
                } catch (error) {
                    // Language not supported by the library
                }
            }
            const allNationalities = nationalitiesLib.getNames('en', { select: 'official' })
            setNationalities(Object.keys(allNationalities).map(key => ({ value: key, label: allNationalities[key] })))
        }

        const fetchData = async () => {
            try {
                await fetchUniqueTypes()
                setUpCountries()
                setUpNationalities()
                setIsLoaded(true)
            } catch (error) {
                toast.error('Error while fetching data. Reloading the page', {
                    onClose: () => window.location.reload(),
                    pauseOnHover: false
                })
            }
        }

        fetchData()
    }, [locales])

    // Sending the data in correct format
    const getSelectedValues = (rangeValue) => {
        if (isMultiSelect) {
            // For Multi Select types like nationality
            const { value } = rows[0]
            if (!value.length)
                throw new Error('Enter/Select a value')
            const selectedVals = []
            const selectedLib = (type.value === 'location' ? countriesLib : nationalitiesLib)
            for (const item of value)
                selectedVals.push({ data: locales.map(localeIter => ({ name: selectedLib.getName(item.value, localeIter.value) || item.label, locale: localeIter.value })) })
            return selectedVals
        }
        else {
            // For other other types
            const selectedVals = rows.map(({ value, locale }) => {
                if (locale === '')
                    throw new Error('Select a locale')
                if (rangeValue) {
                    return {
                        name: rangeValue,
                        locale: locale.value
                    }
                }
                else {
                    if (!value.length)
                        throw new Error('Enter/Select a value')
                    return {
                        name: value,
                        locale: locale.value
                    }
                }
            })
            const selectedLocales = selectedVals.map(item => item.locale)
            for (const localeIter of locales) {
                if (selectedLocales.indexOf(localeIter.value) === -1) {
                    selectedVals.push({ ...selectedVals[0], locale: localeIter.value })
                }
            }
            return [{ data: selectedVals }]
        }
    }

    const resetRows = () => setRows([{ value: '', locale: locales.filter(item => item.isDefault)[0], id: new Date().toISOString() }])

    const closeModalHandler = () => {
        setShowModal(false)
        resetRows()
    }

    const addBtnHandler = async (rangeValue) => {
        if (btnClicked)
            return
        setBtnClicked(true)
        try {
            if (type.value === '')
                throw new Error('Please select a type')
            const resp = await addType({ data: getSelectedValues(rangeValue) }, type.value)
            if (resp.success) {
                toast.success(`${type.value} data entered successfully`)
                setRows(prevRows => prevRows.map(rowEntry => ({ ...rowEntry, value: '' })))
                setTableData([...resp.nodes, ...tableData])
            }
            else
                throw new Error(resp.error)
        } catch (error) {
            toast.error(error.message)
        }
        setBtnClicked(false)
        closeModalHandler()
    }

    const addRowHandler = () => {
        if (rows.length < locales.length)
            setRows([...rows, { value: '', locale: '', id: new Date().toISOString() }])
    }

    const removeRowHandler = (index) => {
        const updatedRows = [...rows]
        updatedRows.splice(index, 1)
        setRows(updatedRows)
    }

    const rowInputChangeHandler = (index, value) => {
        const updatedRows = [...rows]
        updatedRows[index].value = value
        setRows(updatedRows)
    }

    const localesChangeHandler = (index, selectedOption) => {
        if (rows.find((row, i) => (i !== index && row.locale.value === selectedOption.value))) {
            toast.error('This locale has already been selected')
            selectedOption = ''
        }
        const updatedRows = [...rows]
        updatedRows[index].locale = selectedOption
        setRows(updatedRows)
    }

    const typeChangeHandler = (e) => {
        if ([e.target.value, type.value].some(value => ['nationality', 'location'].indexOf(value) !== -1)) {
            resetRows()
        }
        setType({ ...uniqueTypes[e.target.value], value: e.target.value })
    }

    const onEnterHandler = (e) => {
        if (e.key === 'Enter' && !btnClicked)
            addBtnHandler()
    }

    // Setting props
    const TypesProps = { uniqueTypes, typeChangeHandler }
    const AddTypeModalProps = { rowInputChangeHandler, isMultiSelect, type, countries, nationalities, locales, localesChangeHandler, rows, addRowHandler, removeRowHandler, onEnterHandler, btnClicked, addBtnHandler, showModal, closeModalHandler }

    return (
        <>
            {isLoaded
                ? <>
                    <AddTypeModal AddTypeModalProps={AddTypeModalProps} />
                    <h3 className='my-3'>
                        {type.value.length > 0
                            ? `Table showing for ${type.display}`
                            : `Select a type to show its values`}
                    </h3>
                    <div className='d-flex justify-content-between align-items-center mb-2'>
                        <Types TypesProps={TypesProps} />
                        <div className='ms-2'>
                            <Button disabled={type.value.length === 0} variant="success" onClick={() => setShowModal(true)}>
                                Add Main Type
                            </Button>
                        </div>
                    </div>
                </>
                : <Loader />}
        </>)
}

export default TableHeader